import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  CpuChipIcon,
  MagnifyingGlassIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  BeakerIcon,
  CodeBracketIcon,
  SparklesIcon,
  ListBulletIcon,
  ChatBubbleLeftRightIcon,
  QrCodeIcon,
  MegaphoneIcon,
  PhotoIcon,
  ShareIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { agentsAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import ConfirmDeleteDialog from '../components/common/ConfirmDeleteDialog';
import AgentRuntimeBadges from '../components/agents/AgentRuntimeBadges';
import InstallWidgetGuide from '../components/agents/InstallWidgetGuide';
import { agentPublicUrl, hasAgentId, NO_AGENT_LINK_MESSAGE } from '../utils/agentLinks';

const slugifyCampaignValue = (value) => (
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
);

const CAMPAIGN_SOURCE_OPTIONS = [
  { value: 'wati', label: 'WATI' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'qr', label: 'QR Code' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'facebook_ads', label: 'Facebook / Instagram Ads' },
  { value: 'other', label: 'Other / Custom' },
];

const getCampaignSourceValue = (fields) => (
  fields.source === 'other' ? fields.customSource : fields.source
);

const buildCampaignUrl = (agent, fields) => {
  // Campaign URLs (WATI / WhatsApp / SMS / email / ad) point at the
  // dedicated fullscreen chat landing (public/chat.html) — recipient taps
  // the CTA, phone browser opens the page, widget mounts fullscreen and
  // conversation starts immediately. Previously these URLs pointed at the
  // tenant's public agent slug page (getAgentPublicUrl) which wraps the
  // widget in demo/marketing chrome; that page is still useful for organic
  // web visitors but adds a hunt-for-the-bubble step for someone who
  // already clicked a "chat" button on their phone.
  //
  // Uses utm_* naming so WATI's own template preview and downstream
  // analytics (GA, Meta, Serviti's lead campaign attribution) all
  // recognise the params without a mapping step. chat.html reads either
  // utm_source / utm_campaign or campaign_source / campaign_name, so
  // existing consumers still work.
  const url = new URL(`${window.location.origin}/chat.html`);
  const source = slugifyCampaignValue(getCampaignSourceValue(fields)) || 'wati';
  const campaign = slugifyCampaignValue(fields.campaign);
  const project = slugifyCampaignValue(fields.project);

  url.searchParams.set('agent', agent.id);
  url.searchParams.set('utm_source', source);
  if (campaign) url.searchParams.set('utm_campaign', campaign);
  if (project) url.searchParams.set('utm_content', project);
  return url.toString();
};

const buildCampaignMessage = (agent, fields) => {
  const tenantLabel = getQrAgentLabel(agent).replace(/\s+AI Agent$/i, '');
  const projectText = fields.project?.trim() || 'our latest project';

  return `Hi, ${tenantLabel} is sharing details about ${projectText}.

Please find the brochure/details below.

Tap the button below to chat with our AI Agent for pricing, availability, location, and next steps.

Our team will follow up if you share your details.`;
};

const CAMPAIGN_BUTTON_TEXT = 'Chat with AI Agent';

// Inline-styled HTML button so the campaign CTA renders with the Apple-
// Intelligence aurora gradient (same indigo -> magenta -> orange sweep as
// serviti-chat-widget.js) wherever HTML is rendered: email templates,
// landing pages, HTML-rich CMS blocks. WhatsApp / WATI CTA fields still
// need the plain-text label, which is why both copy buttons exist.
// Fully inlined styles + hex colours + no external assets, so it works
// inside strict email clients (Gmail, Outlook, Apple Mail) that strip
// <style> blocks and reject remote resources.
const buildHtmlCampaignButton = (campaignUrl) => (
  `<a href="${campaignUrl}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:8px;padding:12px 22px;background:linear-gradient(135deg,#5E5CE6 0%,#E84CC1 50%,#FF9F0A 100%);color:#ffffff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:15px;font-weight:600;line-height:1;text-decoration:none;border-radius:28px;box-shadow:0 4px 14px rgba(94,92,230,0.35);"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" style="flex-shrink:0;vertical-align:middle"><path fill="#ffffff" d="M12 2.5L13.7 9.1L20.5 10.5L13.7 11.9L12 18.5L10.3 11.9L3.5 10.5L10.3 9.1Z"/><path fill="#ffffff" fill-opacity="0.75" d="M18.5 15L19.2 17.3L21.5 18L19.2 18.7L18.5 21L17.8 18.7L15.5 18L17.8 17.3Z"/></svg>${CAMPAIGN_BUTTON_TEXT}</a>`
);

// NOTE: embed snippets deliberately carry ONLY src + data-agent-id (+ optional
// layout-only data-position / data-theme). Everything else — greeting, title,
// brand logo, brand colour, WhatsApp handoff number/label — is server-owned
// agent config the widget fetches live. It must NOT be baked into a customer's
// snippet, or it freezes the moment they paste and drifts forever after.
// buildBrandingEmbedAttrs / buildWhatsappEmbedAttrs (which did exactly that)
// were removed for this reason — do not reintroduce them.

const getQrAgentLabel = (agent) => {
  const tenantName =
    agent.tenant_name ||
    agent.tenant?.name ||
    agent.business_name ||
    agent.company_name ||
    agent.name ||
    'Serviti';
  const baseName = tenantName
    .replace(/\s+AI\s+Agent$/i, '')
    .replace(/\s+Agent$/i, '')
    .trim();
  return `${baseName || 'Serviti'} AI Agent`;
};

const getQrFileName = (agent) => {
  const name = (agent.slug || getQrAgentLabel(agent))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${name || 'serviti-ai-agent'}-qr.png`;
};

const fitCanvasText = (ctx, text, maxWidth, initialSize, minSize) => {
  let size = initialSize;
  ctx.font = `700 ${size}px Arial, Helvetica, sans-serif`;
  while (size > minSize && ctx.measureText(text).width > maxWidth) {
    size -= 2;
    ctx.font = `700 ${size}px Arial, Helvetica, sans-serif`;
  }
  return size;
};

const AgentManagementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [greetingAgent, setGreetingAgent] = useState(null);
  const [greetingText, setGreetingText] = useState('');
  const [savingGreeting, setSavingGreeting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [filterEnabled, setFilterEnabled] = useState('all'); // all, enabled, disabled
  const [syncing, setSyncing] = useState(false);
  const [embedAgent, setEmbedAgent] = useState(null);
  const [installHosts, setInstallHosts] = useState({});

  // The dashboard's "Install on your website" card links here with
  // ?install=<agent id>, so it opens straight onto the instructions.
  const installRequest = new URLSearchParams(location.search).get('install');
  useEffect(() => {
    if (!installRequest || agents.length === 0) return;
    const match = agents.find((a) => String(a.id) === installRequest) || agents[0];
    if (match) setEmbedAgent(match);
  }, [installRequest, agents]);

  // Which websites each agent's widget has been seen on, so the install
  // dialog can say "live on yoursite.com" instead of leaving it a guess.
  useEffect(() => {
    if (!embedAgent) return undefined;
    let cancelled = false;
    agentsAPI.getInstallStatus()
      .then(({ data }) => {
        if (cancelled) return;
        const byAgent = {};
        (data?.agents || []).forEach((row) => { byAgent[row.agent_id] = row.hosts || []; });
        setInstallHosts(byAgent);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [embedAgent]);
  const [campaignAgent, setCampaignAgent] = useState(null);
  const [campaignFields, setCampaignFields] = useState({
    source: 'wati',
    customSource: '',
    campaign: '',
    project: '',
  });
  const [brandingAgent, setBrandingAgent] = useState(null);
  const [brandingForm, setBrandingForm] = useState({
    brand_name: '',
    brand_logo_url: '',
    brand_header_logo_url: '',
    brand_primary_color: '',
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  
  // Determine the base path (e.g., /platform or /t/tenant-slug)
  const basePath = location.pathname.split('/agent-management')[0];
  const isTenantWorkspace = basePath.startsWith('/t/');
  
  // Role-based access control
  const user = useAuthStore((state) => state.user);
  const isSuperadmin = user?.role === 'superadmin';
  const isTenantAdmin = user?.tenant_role === 'admin' || user?.tenant_role === 'owner';
  const canManageAgents = isSuperadmin;
  const canEditAgentBasics = isSuperadmin || isTenantAdmin;
  const canViewPromptPool = canManageAgents || isTenantAdmin;
  const canGenerateCampaignLinks = canManageAgents || isTenantAdmin;

  const openBrandingModal = (agent) => {
    setBrandingAgent(agent);
    setBrandingForm({
      brand_name: agent.brand_name || '',
      brand_logo_url: agent.brand_logo_url || '',
      brand_header_logo_url: agent.brand_header_logo_url || '',
      brand_primary_color: agent.brand_primary_color || '',
    });
  };

  const handleSaveBranding = async () => {
    if (!brandingAgent) return;
    setSavingBranding(true);
    try {
      await agentsAPI.updateAgentBranding(brandingAgent.id, brandingForm);
      toast.success('Branding updated');
      setBrandingAgent(null);
      await loadAgents();
    } catch (error) {
      console.error('Error updating branding:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update branding');
    } finally {
      setSavingBranding(false);
    }
  };

  // Restore Serviti defaults — clears all 4 brand fields in one atomic PUT
  // (backend converts empty strings to null via _clean_brand_url/text/color).
  // Confirmation guard prevents accidental clicks while scrolling through
  // many agents. Audit trail (brand_updated_source/by/at) captures the
  // reset like any other update so the change is traceable.
  const handleResetBranding = async () => {
    if (!brandingAgent) return;
    const confirmed = window.confirm(
      'Reset branding to Serviti defaults? All custom brand values (name, logos, color) will be cleared and the widget will render Serviti defaults again. This can be re-configured any time.'
    );
    if (!confirmed) return;
    setSavingBranding(true);
    try {
      const cleared = {
        brand_name: '',
        brand_logo_url: '',
        brand_header_logo_url: '',
        brand_primary_color: '',
      };
      await agentsAPI.updateAgentBranding(brandingAgent.id, cleared);
      // Sync the modal form so the visible preview also resets, in case the
      // superadmin keeps the modal open to inspect the reset.
      setBrandingForm(cleared);
      toast.success('Branding reset to Serviti defaults');
      setBrandingAgent(null);
      await loadAgents();
    } catch (error) {
      console.error('Error resetting branding:', error);
      toast.error(error?.response?.data?.detail || 'Failed to reset branding');
    } finally {
      setSavingBranding(false);
    }
  };

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const response = await agentsAPI.getAgents();
      setAgents(response.data?.agents || []);
    } catch (error) {
      console.error('Error loading agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const handleRename = async (agentId) => {
    if (!editName.trim()) {
      toast.error('Please enter an agent name');
      return;
    }

    try {
      await agentsAPI.updateAgent(agentId, { name: editName.trim() });
      toast.success('Agent renamed successfully');
      setEditingId(null);
      loadAgents();
    } catch (error) {
      console.error('Error renaming agent:', error);
      toast.error('Failed to rename agent');
    }
  };

  const openGreetingEditor = (agent) => {
    setGreetingAgent(agent);
    setGreetingText(agent.welcome_message || '');
  };

  const closeGreetingEditor = () => {
    if (savingGreeting) return;
    setGreetingAgent(null);
    setGreetingText('');
  };

  const handleSaveGreeting = async (event) => {
    event.preventDefault();
    if (!greetingAgent) return;

    const nextGreeting = greetingText.trim();
    const currentGreeting = (greetingAgent.welcome_message || '').trim();
    if (nextGreeting === currentGreeting) {
      toast('No changes to save');
      closeGreetingEditor();
      return;
    }

    setSavingGreeting(true);
    try {
      await agentsAPI.updateAgent(greetingAgent.id, { welcome_message: nextGreeting });
      toast.success(nextGreeting ? 'Greeting updated' : 'Greeting reset to the default');
      setGreetingAgent(null);
      setGreetingText('');
      await loadAgents();
    } catch (error) {
      console.error('Error updating agent greeting:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update greeting');
    } finally {
      setSavingGreeting(false);
    }
  };

  const handleDelete = async () => {
    if (!agentToDelete) return;
    try {
      await agentsAPI.deleteAgent(agentToDelete.id);
      toast.success('Agent deleted successfully');
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
      loadAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error('Failed to delete agent');
    }
  };

  const openCampaignLinkHelper = (agent) => {
    setCampaignAgent(agent);
    setCampaignFields({
      source: 'wati',
      customSource: '',
      campaign: '',
      project: '',
    });
  };

  const handleToggleEnabled = async (agent) => {
    try {
      await agentsAPI.updateAgent(agent.id, { enabled: !agent.enabled });
      toast.success(`Agent ${agent.enabled ? 'disabled' : 'enabled'} successfully`);
      loadAgents();
    } catch (error) {
      console.error('Error toggling agent:', error);
      toast.error('Failed to update agent');
    }
  };

  const handleEdit = (agentId) => {
    navigate(`/agents?edit=${agentId}`);
  };

  const handleDuplicate = async (agent) => {
    try {
      const newAgent = {
        ...agent,
        name: `${agent.name} (Copy)`,
        enabled: false
      };
      delete newAgent.id;
      delete newAgent.created_at;
      delete newAgent.updated_at;
      await agentsAPI.createAgent(newAgent);
      toast.success('Agent duplicated successfully');
      loadAgents();
    } catch (error) {
      console.error('Error duplicating agent:', error);
      toast.error('Failed to duplicate agent');
    }
  };

  const handleDownloadQr = async (agent) => {
    const publicUrl = agentPublicUrl(agent);
    if (!publicUrl) {
      toast.error(NO_AGENT_LINK_MESSAGE);
      return;
    }
    try {
      const taggedUrl = new URL(publicUrl, window.location.origin);
      taggedUrl.searchParams.set('utm_source', 'qr');
      const url = taggedUrl.toString();
      const label = getQrAgentLabel(agent);
      const width = 1000;
      const qrSize = 1000;
      const textHeight = 210;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = qrSize + textHeight;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const qrCanvas = document.createElement('canvas');
      await QRCode.toCanvas(qrCanvas, url, {
        errorCorrectionLevel: 'H',
        margin: 3,
        width: qrSize,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      });

      ctx.drawImage(qrCanvas, 0, 0, qrSize, qrSize);

      ctx.fillStyle = '#111827';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '700 44px Arial, Helvetica, sans-serif';
      ctx.fillText('Scan to Chat with', width / 2, qrSize + 54);

      fitCanvasText(ctx, label, width - 96, 58, 34);
      ctx.fillText(label, width / 2, qrSize + 118);

      ctx.fillStyle = '#6b7280';
      ctx.font = '400 24px Arial, Helvetica, sans-serif';
      ctx.fillText('Powered by Serviti AI', width / 2, qrSize + 176);

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = getQrFileName(agent);
      link.click();
      toast.success('QR code downloaded');
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    }
  };

  const startEditing = (agent) => {
    setEditingId(agent.id);
    setEditName(agent.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleRegeneratePrompts = async (agent, { force = false } = {}) => {
    // force = bin every LLM pill and rebuild. Manual pills the admin typed are
    // always kept, but this DOES discard the rest — so ask first.
    if (force) {
      const ok = window.confirm(
        `Rebuild "${agent.name}" opener pills from scratch?\n\n` +
          'This discards every AI-generated pill and regenerates them from the ' +
          'knowledge base. Pills you typed yourself are kept.\n\n' +
          'Use this to clear out a pool full of old, low-quality pills.',
      );
      if (!ok) return;
    }

    const t = toast.loading(
      force
        ? `Rebuilding opener pills for ${agent.name}…`
        : `Regenerating opener pills for ${agent.name}…`,
    );
    try {
      const res = await agentsAPI.regeneratePrompts(agent.id, { force });
      const pills = res.data?.suggested_prompts || [];
      const gaps = res.data?.missing_buyer_slots || [];

      toast.success(
        pills.length
          ? `${pills.length} pill${pills.length === 1 ? '' : 's'}: ${pills.slice(0, 5).join(' • ')}${pills.length > 5 ? ' …' : ''}`
          : 'Generated new pills.',
        { id: t, duration: 6000 },
      );

      // Say what the agent CANNOT offer. A pool with no pricing pill means the
      // most-asked buyer question is never surfaced — and probably cannot be
      // answered from this tenant's content at all. Shipping that quietly is
      // how you onboard broken agents at scale.
      if (gaps.length) {
        toast(
          res.data?.missing_buyer_slots_warning ||
            `No pill for: ${gaps.join(', ')}. Check the knowledge base covers these.`,
          {
            icon: '⚠️',
            duration: 12000,
            style: { background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' },
          },
        );
      }

      loadAgents();
    } catch (error) {
      console.error('Error regenerating prompts:', error);
      const msg =
        error?.response?.data?.detail ||
        'Failed to regenerate pills (KB may be empty).';
      toast.error(msg, { id: t });
    }
  };

  const handleSyncSystemPrompts = async () => {
    try {
      setSyncing(true);
      const response = await agentsAPI.syncSystemPrompts();
      toast.success(response.data?.message || 'System prompts synced successfully');
      loadAgents();
    } catch (error) {
      console.error('Error syncing system prompts:', error);
      toast.error('Failed to sync system prompts');
    } finally {
      setSyncing(false);
    }
  };

  const filteredAgents = agents.filter(a => {
    const matchesSearch = 
      a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.model?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filterEnabled === 'all' ||
      (filterEnabled === 'enabled' && a.enabled) ||
      (filterEnabled === 'disabled' && !a.enabled);
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-4">
            <CpuChipIcon className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AI Agents {canManageAgents ? 'Management' : ''}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {canManageAgents
                  ? 'Manage, edit, rename, and delete your AI agents'
                  : 'View analytics and test your AI agents'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {isSuperadmin && (
              <button
                onClick={handleSyncSystemPrompts}
                disabled={syncing}
                className="flex items-center px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                title="Sync system prompt templates to all agents"
              >
                <ArrowPathIcon className={`w-4 h-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Prompts'}
              </button>
            )}
            <button
              onClick={loadAgents}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {(canManageAgents || (isTenantWorkspace && canEditAgentBasics)) && (
              <button
                onClick={() => navigate(isTenantWorkspace ? `${basePath}/get-started?create=1` : '/agents')}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Create an agent
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1 sm:max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search agents..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <select
          value={filterEnabled}
          onChange={(e) => setFilterEnabled(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Agents</option>
          <option value="enabled">Enabled Only</option>
          <option value="disabled">Disabled Only</option>
        </select>
      </div>

      {/* Agent List */}
      <div className="px-4 sm:px-6 pb-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredAgents.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
            <CpuChipIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
              {searchTerm ? 'No agents found' : 'No agents yet'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {searchTerm ? 'Try adjusting your search' : 'Create your first AI agent to get started'}
            </p>
            {!searchTerm && agents.length === 0 && canEditAgentBasics && (
              <button
                onClick={() => navigate(isTenantWorkspace ? `${basePath}/get-started?create=1` : '/agents')}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Create your first agent
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredAgents.map((agent) => (
              <div
                key={agent.id}
                className={`bg-white dark:bg-gray-800 rounded-lg border ${
                  agent.enabled 
                    ? 'border-gray-200 dark:border-gray-700' 
                    : 'border-gray-200 dark:border-gray-700 opacity-60'
                } p-4 hover:shadow-md transition-shadow`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {editingId === agent.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRename(agent.id);
                            if (e.key === 'Escape') cancelEditing();
                          }}
                          className="flex-1 px-3 py-1 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          autoFocus
                        />
                        <button
                          onClick={() => handleRename(agent.id)}
                          className="p-1 text-green-600 hover:text-green-700"
                          title="Save"
                        >
                          <CheckIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Cancel"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                            {agent.name}
                          </h3>
                          {agent.enabled ? (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                              <CheckCircleIcon className="w-3 h-3 mr-1" />
                              Enabled
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                              <XCircleIcon className="w-3 h-3 mr-1" />
                              Disabled
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                          {agent.description || 'No description'}
                        </p>
                        {agent.knowledge_connected === false && (
                          <button
                            type="button"
                            onClick={() => handleEdit(agent.id)}
                            className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                          >
                            Reconnect this agent&apos;s knowledge
                          </button>
                        )}
                        <div className="flex items-center flex-wrap gap-2 mt-2">
                          <AgentRuntimeBadges />
                          {agent.use_rag && (
                            <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                              Uses business knowledge
                            </span>
                          )}
                          {agent.workflow && (
                            <span className="px-2 py-0.5 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded">
                              🔄 Workflow
                            </span>
                          )}
                          {agent.prompt_template_name && (
                            <span className="px-2 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded">
                              📝 {agent.prompt_template_name.replace('.json', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {editingId !== agent.id && (
                    <div className="flex flex-wrap items-start gap-2 sm:ml-4 sm:justify-end">
                      <button
                        onClick={() => {
                          const publicUrl = agentPublicUrl(agent);
                          if (publicUrl) window.open(publicUrl, '_blank');
                          else toast.error(NO_AGENT_LINK_MESSAGE);
                        }}
                        className="inline-flex min-h-[40px] items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                      >
                        <BeakerIcon className="w-4 h-4" />
                        Open
                      </button>

                      <button
                        type="button"
                        onClick={() => setEmbedAgent(agent)}
                        className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-green-600 bg-white px-3 py-2 text-sm font-semibold text-green-700 hover:bg-green-50 dark:bg-gray-800 dark:text-green-300 dark:hover:bg-gray-700"
                      >
                        <CodeBracketIcon className="w-4 h-4" />
                        Install on website
                      </button>

                      <div className="relative" data-testid={`agent-share-${agent.id}`}>
                        <button
                          type="button"
                          aria-haspopup="menu"
                          aria-expanded={openActionMenu === `${agent.id}:share`}
                          onClick={() => setOpenActionMenu((current) => (
                            current === `${agent.id}:share` ? null : `${agent.id}:share`
                          ))}
                          className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                        >
                          <ShareIcon className="w-4 h-4" />
                          Share
                          <ChevronDownIcon className="w-3.5 h-3.5" />
                        </button>
                        {openActionMenu === `${agent.id}:share` && (
                        <div className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                          <button
                            onClick={() => {
                              setOpenActionMenu(null);
                              const publicUrl = agentPublicUrl(agent);
                              if (!publicUrl) {
                                toast.error(NO_AGENT_LINK_MESSAGE);
                                return;
                              }
                              navigator.clipboard.writeText(publicUrl);
                              toast.success('Agent link copied!');
                            }}
                            className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          >
                            <DocumentDuplicateIcon className="w-4 h-4" />
                            Copy agent link
                          </button>
                          {canGenerateCampaignLinks && (
                            <button
                              onClick={() => { setOpenActionMenu(null); openCampaignLinkHelper(agent); }}
                              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <MegaphoneIcon className="w-4 h-4" />
                              Create campaign link
                            </button>
                          )}
                          <button
                            onClick={() => { setOpenActionMenu(null); handleDownloadQr(agent); }}
                            className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          >
                            <QrCodeIcon className="w-4 h-4" />
                            Download QR code
                          </button>
                          <button
                            onClick={() => { setOpenActionMenu(null); setEmbedAgent(agent); }}
                            className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          >
                            <CodeBracketIcon className="w-4 h-4" />
                            Install on website
                          </button>
                        </div>
                        )}
                      </div>

                      <div className="relative" data-testid={`agent-settings-${agent.id}`}>
                        <button
                          type="button"
                          aria-haspopup="menu"
                          aria-expanded={openActionMenu === `${agent.id}:settings`}
                          onClick={() => setOpenActionMenu((current) => (
                            current === `${agent.id}:settings` ? null : `${agent.id}:settings`
                          ))}
                          className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                        >
                          <Cog6ToothIcon className="w-4 h-4" />
                          Settings
                          <ChevronDownIcon className="w-3.5 h-3.5" />
                        </button>
                        {openActionMenu === `${agent.id}:settings` && (
                        <div className="absolute right-0 z-20 mt-2 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-800">
                          <button
                            onClick={() => { setOpenActionMenu(null); navigate(`${basePath}/analytics?agent=${agent.id}`); }}
                            className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          >
                            <ChartBarIcon className="w-4 h-4" />
                            View analytics
                          </button>
                          {(agent.branding_configurable || isSuperadmin) && (
                            <button
                              onClick={() => { setOpenActionMenu(null); openBrandingModal(agent); }}
                              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <PhotoIcon className="w-4 h-4" />
                              Branding
                            </button>
                          )}
                          {/* Toggle Enable/Disable - Admin only */}
                          {canManageAgents && (
                            <button
                              onClick={() => { setOpenActionMenu(null); handleToggleEnabled(agent); }}
                              className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                agent.enabled
                                  ? 'text-green-700 dark:text-green-300'
                                  : 'text-gray-700 dark:text-gray-200'
                              }`}
                            >
                              {agent.enabled ? (
                                <CheckCircleIcon className="w-4 h-4" />
                              ) : (
                                <XCircleIcon className="w-4 h-4" />
                              )}
                              <span>{agent.enabled ? 'Disable agent' : 'Enable agent'}</span>
                            </button>
                          )}
                          {/* Edit Config - Admin only */}
                          {canManageAgents && (
                            <button
                              onClick={() => { setOpenActionMenu(null); handleEdit(agent.id); }}
                              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <Cog6ToothIcon className="w-4 h-4" />
                              <span>Advanced configuration</span>
                            </button>
                          )}
                          {/* Regenerate KB-grounded opener pills - Admin only.
                              Plain click = add new pills + prune ones that fail
                              the quality gate. Shift-click = rebuild from
                              scratch, binning every AI-generated pill (manual
                              pills are always kept). Without the rebuild there
                              is no way to clear a pool that predates the gate. */}
                          {canManageAgents && (
                            <button
                              onClick={(e) => {
                                setOpenActionMenu(null);
                                handleRegeneratePrompts(agent, { force: e.shiftKey });
                              }}
                              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              title={
                                'Regenerate opener pills from the knowledge base.\n' +
                                'Shift-click to REBUILD: discards every AI-generated pill and ' +
                                'starts fresh (pills you typed yourself are kept).'
                              }
                            >
                              <SparklesIcon className="w-4 h-4" />
                              <span>Regenerate suggested questions</span>
                            </button>
                          )}
                          {/* Prompt pool insight - tenant admins can view; superadmins can edit */}
                          {canViewPromptPool && (
                            <button
                              onClick={() => {
                                setOpenActionMenu(null);
                                navigate(`${basePath}/agent-management/${agent.id}/prompt-pool`);
                              }}
                              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <ListBulletIcon className="w-4 h-4" />
                              <span>Suggested questions</span>
                            </button>
                          )}
                          {/* Tenant admins can change only the operating basics. */}
                          {canEditAgentBasics && (
                            <button
                              onClick={() => { setOpenActionMenu(null); openGreetingEditor(agent); }}
                              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <ChatBubbleLeftRightIcon className="w-4 h-4" />
                              <span>Edit greeting</span>
                            </button>
                          )}
                          {canEditAgentBasics && (
                            <button
                              onClick={() => { setOpenActionMenu(null); startEditing(agent); }}
                              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <PencilIcon className="w-4 h-4" />
                              <span>Rename agent</span>
                            </button>
                          )}
                          {/* Duplicate - Admin only */}
                          {canManageAgents && (
                            <button
                              onClick={() => { setOpenActionMenu(null); handleDuplicate(agent); }}
                              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                              <DocumentDuplicateIcon className="w-4 h-4" />
                              <span>Duplicate agent</span>
                            </button>
                          )}
                          {/* Delete - Admin only */}
                          {canManageAgents && (
                            <button
                              onClick={() => {
                                setOpenActionMenu(null);
                                setAgentToDelete(agent);
                                setDeleteDialogOpen(true);
                              }}
                              className="flex w-full items-center gap-3 border-t border-gray-100 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:border-gray-700 dark:text-red-400 dark:hover:bg-red-900/20"
                            >
                              <TrashIcon className="w-4 h-4" />
                              <span>Delete agent</span>
                            </button>
                          )}
                        </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setAgentToDelete(null); }}
        onConfirm={handleDelete}
        title="Delete Agent"
        entityName={agentToDelete?.name || ''}
        warningText="This will permanently delete this agent and all its configuration. This action cannot be undone."
        confirmButtonText="Delete Agent"
      />

      {greetingAgent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <form
            onSubmit={handleSaveGreeting}
            className="bg-white rounded-xl shadow-2xl w-full max-w-xl"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Edit greeting</h2>
                <p className="mt-1 text-sm text-gray-500">{greetingAgent.name}</p>
              </div>
              <button
                type="button"
                onClick={closeGreetingEditor}
                disabled={savingGreeting}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                aria-label="Close greeting editor"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <label htmlFor="agent-greeting" className="block text-sm font-medium text-gray-700 mb-2">
                Welcome message
              </label>
              <textarea
                id="agent-greeting"
                value={greetingText}
                onChange={(event) => setGreetingText(event.target.value)}
                rows={5}
                maxLength={2000}
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Hello! How can I help you today?"
              />
              <p className="mt-2 text-xs text-gray-500">
                Leave blank to use the template or default greeting. Suggested questions stay separate and continue to come from your knowledge base.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={closeGreetingEditor}
                disabled={savingGreeting}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingGreeting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg"
              >
                {savingGreeting ? 'Saving…' : 'Save greeting'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Agent Branding Modal */}
      {brandingAgent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-[720px] max-w-[92vw] max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-pink-600 to-indigo-600">
              <div className="flex items-center gap-3">
                <PhotoIcon className="w-6 h-6 text-white" />
                <h2 className="text-lg font-semibold text-white">Agent Branding</h2>
              </div>
              <button
                onClick={() => setBrandingAgent(null)}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                disabled={savingBranding}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)]">
              {!brandingAgent.branding_allowed && (
                <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  Custom branding is public only on Scale. Superadmins can prepare values here; visitors will still see Serviti branding until the tenant is on Scale.
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Brand Name</label>
                    <input
                      type="text"
                      value={brandingForm.brand_name}
                      onChange={(e) => setBrandingForm({ ...brandingForm, brand_name: e.target.value })}
                      placeholder={brandingAgent.name}
                      maxLength={120}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Greeting Logo URL</label>
                    <input
                      type="url"
                      value={brandingForm.brand_logo_url}
                      onChange={(e) => setBrandingForm({ ...brandingForm, brand_logo_url: e.target.value })}
                      placeholder="https://example.com/logo.png"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Use an HTTPS image URL. Leave blank to use Serviti default.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Header Logo URL</label>
                    <input
                      type="url"
                      value={brandingForm.brand_header_logo_url}
                      onChange={(e) => setBrandingForm({ ...brandingForm, brand_header_logo_url: e.target.value })}
                      placeholder="https://example.com/logo-white.png"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Optional. Use a light/white logo for the dark header.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Primary Color</label>
                    <input
                      type="text"
                      value={brandingForm.brand_primary_color}
                      onChange={(e) => setBrandingForm({ ...brandingForm, brand_primary_color: e.target.value })}
                      placeholder="#0066FF"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Preview</label>
                  <div className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50">
                    <div
                      className="px-4 py-3 text-white flex items-center gap-3"
                      style={{ background: brandingForm.brand_primary_color || '#E84CC1' }}
                    >
                      {brandingForm.brand_header_logo_url || brandingForm.brand_logo_url ? (
                        <img
                          src={brandingForm.brand_header_logo_url || brandingForm.brand_logo_url}
                          alt=""
                          className="w-10 h-10 object-contain rounded"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-white/20 flex items-center justify-center text-xs font-bold">AI</div>
                      )}
                      <div>
                        <div className="font-semibold">{brandingForm.brand_name || brandingAgent.name}</div>
                        <div className="text-xs text-white/80">Ready to help you!</div>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
                        {brandingForm.brand_logo_url && (
                          <img
                            src={brandingForm.brand_logo_url}
                            alt=""
                            className="w-11 h-11 object-contain rounded mb-2"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                        Hello! How can I help you today?
                      </div>
                      <div className="mt-3 text-[11px] text-center text-gray-400">
                        Powered by Serviti
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                {/* Reset lives left-aligned as a subordinate destructive action;
                    Save/Cancel stay grouped right. Disabled when there's nothing
                    to reset (all 4 fields already empty) so the button doesn't
                    invite pointless clicks. */}
                <button
                  type="button"
                  onClick={handleResetBranding}
                  disabled={
                    savingBranding ||
                    !(
                      brandingForm.brand_name ||
                      brandingForm.brand_logo_url ||
                      brandingForm.brand_header_logo_url ||
                      brandingForm.brand_primary_color
                    )
                  }
                  className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Clear custom brand values and revert to Serviti defaults"
                >
                  Reset to Serviti defaults
                </button>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setBrandingAgent(null)}
                    disabled={savingBranding}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveBranding}
                    disabled={savingBranding}
                    className="px-4 py-2 text-white bg-pink-600 hover:bg-pink-700 rounded-lg disabled:opacity-60"
                  >
                    {savingBranding ? 'Saving...' : 'Save Branding'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Link Helper Modal */}
      {hasAgentId(campaignAgent) && (() => {
        const campaignUrl = buildCampaignUrl(campaignAgent, campaignFields);
        const campaignMessage = buildCampaignMessage(campaignAgent, campaignFields);
        const campaignSlug = slugifyCampaignValue(campaignFields.campaign);
        const sourceSlug = slugifyCampaignValue(getCampaignSourceValue(campaignFields)) || 'wati';
        const projectSlug = slugifyCampaignValue(campaignFields.project);
        const canCopyCampaignUrl = Boolean(campaignSlug);
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-[680px] max-w-[92vw] max-h-[85vh] overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-cyan-600 to-blue-600">
                <div className="flex items-center gap-3">
                  <MegaphoneIcon className="w-6 h-6 text-white" />
                  <h2 className="text-lg font-semibold text-white">Campaign CTA Helper</h2>
                </div>
                <button
                  onClick={() => setCampaignAgent(null)}
                  className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)]">
                <p className="text-gray-600 mb-5">
                  Use the <strong>{campaignAgent.name.replace(/\s+AI\s+Agent$/i, '') || campaignAgent.name}</strong> AI Agent as a campaign CTA button &mdash; works with WATI, WhatsApp, SMS, email, ads and QR codes. Leads captured from that button will carry source and campaign attribution.
                </p>

                <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
                  <p className="font-semibold mb-2">How to use this campaign link</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Copy the message body into your campaign template or message.</li>
                    <li>Add a CTA URL button (or paste the link directly into your message if the channel has no button).</li>
                    <li>Set button text to <strong>{CAMPAIGN_BUTTON_TEXT}</strong>.</li>
                    <li>Paste the Serviti button URL into your CTA URL field.</li>
                  </ol>
                  <p className="mt-3 text-xs text-blue-800 opacity-90">
                    When a recipient taps the button, their phone opens a fullscreen chat with your AI agent &mdash; no landing page, no menu, straight into a conversation. Campaign source and name flow through so captured leads carry the attribution automatically.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Source</label>
                    <select
                      value={campaignFields.source}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      onChange={(e) => setCampaignFields({
                        ...campaignFields,
                        source: e.target.value,
                        customSource: e.target.value === 'other' ? campaignFields.customSource : '',
                      })}
                    >
                      {CAMPAIGN_SOURCE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    {campaignFields.source === 'other' && (
                      <input
                        type="text"
                        value={campaignFields.customSource}
                        onChange={(e) => setCampaignFields({ ...campaignFields, customSource: e.target.value })}
                        placeholder="Example: event_booth"
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Campaign Name *</label>
                    <input
                      type="text"
                      value={campaignFields.campaign}
                      onChange={(e) => setCampaignFields({ ...campaignFields, campaign: e.target.value })}
                      placeholder="Example: ark_xyz_launch"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                        canCopyCampaignUrl ? 'border-gray-300' : 'border-amber-300 bg-amber-50'
                      }`}
                    />
                    {!canCopyCampaignUrl && (
                      <p className="mt-1 text-xs text-amber-700">Required for campaign attribution.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Project / Offer</label>
                    <input
                      type="text"
                      value={campaignFields.project}
                      onChange={(e) => setCampaignFields({ ...campaignFields, project: e.target.value })}
                      placeholder="Example: Ark XYZ"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>

                <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
                  <p className="font-semibold text-gray-800 mb-2">Lead attribution preview</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-gray-700">
                    <p><span className="font-medium">Source:</span> {sourceSlug}</p>
                    <p><span className="font-medium">Campaign:</span> {campaignSlug || 'Enter campaign name'}</p>
                    <p><span className="font-medium">Project:</span> {projectSlug || 'Optional'}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-700">Campaign Link URL</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          if (!canCopyCampaignUrl) {
                            toast.error('Enter campaign name before opening the button URL');
                            return;
                          }
                          window.open(campaignUrl, '_blank', 'noopener,noreferrer');
                        }}
                        disabled={!canCopyCampaignUrl}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          canCopyCampaignUrl
                            ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Open link
                      </button>
                      <button
                        onClick={() => {
                          if (!canCopyCampaignUrl) {
                            toast.error('Enter campaign name before copying the button URL');
                            return;
                          }
                          navigator.clipboard.writeText(campaignUrl);
                          toast.success('Campaign link copied!');
                        }}
                        disabled={!canCopyCampaignUrl}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          canCopyCampaignUrl
                            ? 'text-cyan-700 hover:text-cyan-800 hover:bg-cyan-50'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                        Copy URL
                      </button>
                    </div>
                  </div>
                  <div className={`border rounded-lg p-3 text-sm break-all ${
                    canCopyCampaignUrl
                      ? 'bg-gray-50 border-gray-200 text-gray-800'
                      : 'bg-amber-50 border-amber-200 text-amber-800'
                  }`}>
                    {canCopyCampaignUrl ? campaignUrl : 'Enter a campaign name to generate a tracked CTA URL.'}
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-700">Button Text</label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(CAMPAIGN_BUTTON_TEXT);
                          toast.success('Button text copied!');
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-cyan-700 hover:text-cyan-800 hover:bg-cyan-50 rounded-lg transition-colors"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                        Copy Text
                      </button>
                      <button
                        onClick={() => {
                          if (!canCopyCampaignUrl) {
                            toast.error('Enter campaign name before copying the HTML button');
                            return;
                          }
                          navigator.clipboard.writeText(buildHtmlCampaignButton(campaignUrl));
                          toast.success('HTML button copied!');
                        }}
                        disabled={!canCopyCampaignUrl}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          canCopyCampaignUrl
                            ? 'text-cyan-700 hover:text-cyan-800 hover:bg-cyan-50'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                        Copy HTML Button
                      </button>
                    </div>
                  </div>
                  {/* Rendered preview of the HTML button in Apple-Intelligence
                      aurora colours -- the same gradient the collapsed widget
                      uses (serviti-chat-widget.js .serviti-chat-button). Gives
                      marketers a visual of what recipients will see in HTML-
                      capable channels before they publish. */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex justify-center">
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 22px',
                        background: 'linear-gradient(135deg,#5E5CE6 0%,#E84CC1 50%,#FF9F0A 100%)',
                        color: '#ffffff',
                        fontSize: '15px',
                        fontWeight: 600,
                        lineHeight: 1,
                        borderRadius: '28px',
                        boxShadow: '0 4px 14px rgba(94,92,230,0.35)',
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" style={{ flexShrink: 0 }}>
                        <path fill="#ffffff" d="M12 2.5L13.7 9.1L20.5 10.5L13.7 11.9L12 18.5L10.3 11.9L3.5 10.5L10.3 9.1Z" />
                        <path fill="#ffffff" fillOpacity="0.75" d="M18.5 15L19.2 17.3L21.5 18L19.2 18.7L18.5 21L17.8 18.7L15.5 18L17.8 17.3Z" />
                      </svg>
                      {CAMPAIGN_BUTTON_TEXT}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    <strong>Copy Text</strong> &mdash; paste into the CTA button field of WATI, Gallabox, Aisensy or any BSP (WhatsApp buttons are rendered by WhatsApp itself, so plain text is required).
                    &nbsp;<strong>Copy HTML Button</strong> &mdash; paste into email campaigns, landing pages, Notion, or any HTML-capable surface to get the styled Serviti button shown above.
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-700">Message Body</label>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(campaignMessage);
                        toast.success('Message body copied!');
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-cyan-700 hover:text-cyan-800 hover:bg-cyan-50 rounded-lg transition-colors"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                      Copy Body
                    </button>
                  </div>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm whitespace-pre-wrap break-words">
                    <code>{campaignMessage}</code>
                  </pre>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button
                  onClick={() => setCampaignAgent(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Embed Code Modal */}
      {embedAgent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="install-dialog-title"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[700px] max-w-[92vw] max-h-[88vh] overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600">
              <div className="flex items-center gap-3">
                <CodeBracketIcon className="w-6 h-6 text-white" />
                <h2 id="install-dialog-title" className="text-lg font-semibold text-white">
                  Install {((embedAgent.branding_allowed && embedAgent.brand_name) || embedAgent.name || 'your agent')} on your website
                </h2>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setEmbedAgent(null)}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(88vh-140px)]">
              {hasAgentId(embedAgent)
                ? <InstallWidgetGuide agent={embedAgent} hosts={installHosts[embedAgent.id] || []} />
                : <p className="text-sm text-gray-700 dark:text-gray-300">{NO_AGENT_LINK_MESSAGE}</p>}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-end">
              <button
                type="button"
                onClick={() => setEmbedAgent(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentManagementPage;
